import { useState } from "react";
import { useTranslation } from "../localization/useTranslation";
import { IAuditLogDialog } from "./AuditLogDialog";
import { AuditEntriesPreset, AuditLogEntryViewModel, AuditLogPropertyType, AuditLogPropertyViewModel, AuditLogViewModel } from "./AuditLogDialog.types";
import { isNullOrWhitespace } from "../common/stringOperations";
import { ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { EntityType } from "../common/Common.types.ts";
import { useAllSelectableUsersQuery } from "@common/hooks/useSelectableItemQueries";
import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";

export function AuditLogDialogHooks(props: IAuditLogDialog) {
    const { translateString, translateDate } = useTranslation();
    const [selectedLog, setSelectedLog] = useState<string | null>(null);
    const [properties, setProperties] = useState<Array<AuditLogPropertyViewModel>>([]);

    const [preset, setPreset] = useState<AuditEntriesPreset | null>("Past12Months");
    const [from, setFrom] = useState<Date | null>(null);
    const [to, setTo] = useState<Date | null>(null);
    const [userId, setUserId] = useState<string | null>(null);

    const url = buildAuditLogUrl(props.entityId, preset, from, to, userId);
    const { isLoading, data } = useDotLegalQuery<AuditLogViewModel>(url);

    const { data: users } = useAllSelectableUsersQuery();

    const presetOptions: Array<{ id: AuditEntriesPreset; name: string }> = [
        { id: "Past12Months", name: translateString("past12Months") },
        { id: "Past6Months", name: translateString("past6Months") },
        { id: "Past3Months", name: translateString("past3Months") },
        { id: "Past30Days", name: translateString("past30Days") },
        { id: "ThisMonth", name: translateString("thisMonth") },
    ];

    function onPresetChange(newPreset: AuditEntriesPreset | null) {
        setPreset(newPreset);
        setFrom(null);
        setTo(null);
    }

    function onFromChange(newFrom: Date | null) {
        setFrom(newFrom);
        setPreset(null);
    }

    function onToChange(newTo: Date | null) {
        setTo(newTo);
        setPreset(null);
    }

    let tableEntryHeader: Array<ITableHeader<AuditLogEntryViewModel>> = [
        { property: "auditType", text: translateString("action"), align: "left", showOnMobile: true },
        { property: "timeStamp", text: translateString("timeStamp"), align: "left", showOnMobile: true },
        { property: "name", text: translateString("name"), align: "left", showOnMobile: true },
    ];

    let tablePriorityHeader: Array<ITableHeader<AuditLogPropertyViewModel>> = [
        { property: "propertyName", text: translateString("input"), align: "left", showOnMobile: true },
        { property: "from", text: translateString("updatedFrom"), align: "left", showOnMobile: true },
        { property: "to", text: translateString("updatedTo"), align: "left", showOnMobile: true },
    ];

    function getHeader() {
        switch (props.entityType) {
            case EntityType.ProcessingActivity:
                return translateString("auditLogProcessingActivityHeader");
            case EntityType.AnnualWheelActivity:
                return translateString("auditLogAnnualWheelHeader");
            case EntityType.Risk:
                return translateString("auditLogRiskHeader");
            case EntityType.System:
                return translateString("auditLogSystemHeader");
            case EntityType.Task:
                return translateString("auditLogTaskManagementHeader");
            case EntityType.Contract:
                return translateString("auditLogContractHeader");
            case EntityType.ContractDocument:
                return translateString("auditLogContractDocumentHeader");
            case EntityType.LegalEntityDocument:
                return translateString("auditLogLegalEntityDocumentHeader");
            case EntityType.ProcessingActivityDocument:
                return translateString("auditLogProcessingActivityDocumentHeader");
            case EntityType.SystemDocument:
                return translateString("auditLogSystemDocumentHeader");
            case EntityType.PolicyDocument:
                return translateString("auditLogPolicyDocumentHeader");
            case EntityType.IncidentLogDocument:
                return translateString("auditLogIncidentLogDocumentHeader");
            case EntityType.ProcessDocument:
                return translateString("auditLogProcessDocumentHeader");
        }
    }

    return {
        isLoading,
        data,
        getHeader,
        selectedLog,
        setSelectedLog,
        tableEntryHeader,
        tablePriorityHeader,
        properties,
        setProperties,
        translateValue,
        preset,
        from,
        to,
        userId,
        presetOptions,
        users: users ?? [],
        onPresetChange,
        onFromChange,
        onToChange,
        setUserId,
    };

    function translateValue(value: string | null, propertyType: AuditLogPropertyType, propertyName: string) {
        if (isNullOrWhitespace(value ?? undefined)) return value;

        if (propertyType === AuditLogPropertyType.Enum) return value;

        if (propertyType === AuditLogPropertyType.Date) {
            return translateDate(new Date(value!));
        }

        if (!isNullOrWhitespace(value ?? undefined)) {
            if (value!.toLowerCase() === "false" || value!.toLowerCase() === "true") {
                return translateString(value!.toLowerCase());
            }
        }

        return value;
    }
}

function buildAuditLogUrl(entityId: string | undefined, preset: AuditEntriesPreset | null, from: Date | null, to: Date | null, userId: string | null) {
    const params = new URLSearchParams();
    if (preset) {
        params.set("preset", preset);
    } else {
        if (from) params.set("from", toIsoDate(from));
        if (to) params.set("to", toIsoDate(to));
    }
    if (userId) params.set("userId", userId);

    const queryString = params.toString();
    return queryString ? `/auditlog/${entityId}?${queryString}` : `/auditlog/${entityId}`;
}

function toIsoDate(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}
